import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { kg, money, num } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Truck, Package, Weight, IndianRupee, Boxes, Building2, User, ClipboardCheck, AlertTriangle, TrendingUp, Undo2 } from "lucide-react";
import DateDMY from "@/components/DateDMY";

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};

function Kpi({ icon: Icon, label, value, tint = "blue", testid }) {
  const bg = { blue: "bg-blue-50 text-blue-700", emerald: "bg-emerald-50 text-emerald-700", amber: "bg-amber-50 text-amber-700", violet: "bg-violet-50 text-violet-700", rose: "bg-rose-50 text-rose-700", slate: "bg-slate-100 text-slate-700" }[tint];
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className={`h-10 w-10 rounded-lg grid place-items-center ${bg}`}><Icon className="h-5 w-5" /></div>
      </div>
      <div className="mt-4 text-sm text-slate-500">{label}</div>
      <div className="font-heading font-bold text-2xl text-slate-900 font-mono-num mt-1">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);

  const load = async () => {
    const [d, t] = await Promise.all([
      api.get("/reports/dashboard", { params: { from_date: from, to_date: to } }),
      api.get("/reports/daily-cost-trend", { params: { from_date: from, to_date: to } }),
    ]);
    setData(d.data);
    setTrend(t.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Operational overview for the selected period.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div><Label>From</Label><DateDMY value={from} onChange={setFrom} testid="dashboard-from-date" /></div>
          <div><Label>To</Label><DateDMY value={to} onChange={setTo} minDate={from} testid="dashboard-to-date" /></div>
          <Button onClick={load} className="bg-blue-600 hover:bg-blue-700" data-testid="dashboard-apply-btn">Apply</Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={Truck} label="Total Dispatches" value={num(data.total_dispatches, 0)} tint="blue" testid="kpi-total-dispatches" />
            <Kpi icon={Weight} label="Total Weight" value={kg(data.total_weight)} tint="emerald" testid="kpi-total-weight" />
            <Kpi icon={IndianRupee} label="Approved Freight" value={money(data.total_freight)} tint="amber" testid="kpi-total-freight" />
            <Kpi icon={IndianRupee} label="Avg Cost / Kg" value={money(data.avg_cost_per_kg)} tint="violet" testid="kpi-avg-cost" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Kpi icon={Package} label="SMP Weight" value={kg(data.smp_weight)} tint="slate" testid="kpi-smp-weight" />
            <Kpi icon={Package} label="Desi Ghee Weight" value={kg(data.ghee_weight)} tint="slate" testid="kpi-ghee-weight" />
            <Kpi icon={Package} label="Loose Ghee Weight" value={kg(data.loose_ghee_weight)} tint="slate" testid="kpi-loose-ghee-weight" />
            <Kpi icon={Boxes} label="Atomizer Qty" value={num(data.atomizer_qty, 0)} tint="slate" testid="kpi-atomizer-qty" />
            <Kpi icon={Boxes} label="Nozzle Qty" value={num(data.nozzle_qty, 0)} tint="slate" testid="kpi-nozzle-qty" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Kpi icon={Truck} label="Transporter Vehicle Weight" value={kg(data.transporter_veh_weight)} tint="blue" testid="kpi-transporter-veh" />
            <Kpi icon={Building2} label="Company Vehicle Weight" value={kg(data.company_veh_weight)} tint="emerald" testid="kpi-company-veh" />
            <Kpi icon={User} label="Customer Vehicle Weight" value={kg(data.customer_veh_weight)} tint="rose" testid="kpi-customer-veh" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Kpi icon={Weight} label="Gross Dispatched Weight" value={kg(data.total_weight)} tint="slate" testid="kpi-gross-weight" />
            <Kpi icon={Undo2} label="Returned Weight" value={kg(data.returned_weight)} tint="amber" testid="kpi-returned-weight" />
            <Kpi icon={Weight} label="Net Weight" value={kg(data.net_weight)} tint="emerald" testid="kpi-net-weight" />
          </div>

          {/* POD Analytics */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="pod-analytics-tile">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-blue-600" />
                <h3 className="font-heading font-semibold text-lg">POD Collection Health</h3>
              </div>
              {data.overdue_count > 0 && (
                <div className="flex items-center gap-1.5 text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-md px-2.5 py-1" data-testid="overdue-pod-badge">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span><b className="font-mono-num">{num(data.overdue_count, 0)}</b> overdue ({data.overdue_days_threshold}+ days)</span>
                </div>
              )}
            </div>
            {(() => {
              const total = (data.pod_counts?.Pending || 0) + (data.pod_counts?.Received || 0) + (data.pod_counts?.Damaged || 0) + (data.pod_counts?.Missing || 0);
              const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);
              return (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    {[
                      { k: "Pending", tint: "bg-slate-100 text-slate-700", ring: "bg-slate-400" },
                      { k: "Received", tint: "bg-emerald-50 text-emerald-700", ring: "bg-emerald-500" },
                      { k: "Damaged", tint: "bg-rose-50 text-rose-700", ring: "bg-rose-500" },
                      { k: "Missing", tint: "bg-amber-50 text-amber-700", ring: "bg-amber-500" },
                    ].map(({ k, tint, ring }) => (
                      <div key={k} className={`rounded-lg p-3 ${tint}`} data-testid={`pod-tile-${k.toLowerCase()}`}>
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
                          <span className={`h-2 w-2 rounded-full ${ring}`}></span> {k}
                        </div>
                        <div className="mt-1 font-heading font-bold text-2xl font-mono-num">{num(data.pod_counts?.[k] || 0, 0)}</div>
                        <div className="text-xs mt-0.5">{kg(data.pod_weight?.[k] || 0)} · {pct(data.pod_counts?.[k] || 0)}%</div>
                      </div>
                    ))}
                  </div>
                  {total > 0 && (
                    <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
                      <div className="bg-slate-400" style={{ width: `${pct(data.pod_counts?.Pending || 0)}%` }} title={`Pending ${pct(data.pod_counts?.Pending || 0)}%`}></div>
                      <div className="bg-emerald-500" style={{ width: `${pct(data.pod_counts?.Received || 0)}%` }} title={`Received ${pct(data.pod_counts?.Received || 0)}%`}></div>
                      <div className="bg-rose-500" style={{ width: `${pct(data.pod_counts?.Damaged || 0)}%` }} title={`Damaged ${pct(data.pod_counts?.Damaged || 0)}%`}></div>
                      <div className="bg-amber-500" style={{ width: `${pct(data.pod_counts?.Missing || 0)}%` }} title={`Missing ${pct(data.pod_counts?.Missing || 0)}%`}></div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Cost/Kg Daily Trend */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="cost-trend-card">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                <h3 className="font-heading font-semibold text-lg">Transport Cost / Kg — Daily Trend</h3>
              </div>
              <div className="text-xs text-slate-500 font-mono-num">{trend.length} day{trend.length === 1 ? "" : "s"}</div>
            </div>
            {trend.length === 0 ? (
              <div className="h-52 grid place-items-center text-slate-400 text-sm">No data in this range.</div>
            ) : (
              <div className="h-64" data-testid="cost-trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickMargin={6} />
                    <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => "₹" + v} />
                    <Tooltip formatter={(v, name) => name === "cost_per_kg" ? [money(v) + "/Kg", "Cost / Kg"] : [v, name]}
                      labelFormatter={(l) => `Date: ${l}`}
                      contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    <Line type="monotone" dataKey="cost_per_kg" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3, fill: "#2563eb" }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
